/**
 * Marker version indicating a non-release build/branch.
 * When the app version is this value, the UI should display the git hash instead.
 */
export const NON_RELEASE_VERSION = '0.0.0';

export function isNonReleaseVersion(version: string): boolean {
  return version === NON_RELEASE_VERSION;
}

/**
 * Computes the latest (highest) release version from tours and showcases.
 * Used to set firstSeenVersion for new users when the app version is 0.0.0.
 * @param tours - Record of tours with releasedInVersion
 * @param showcases - Record of showcases with releasedInVersion
 * @returns The highest version string, or NON_RELEASE_VERSION if both are empty
 */
export function getLatestReleaseVersion(
  tours: Record<string, { releasedInVersion: string }>,
  showcases: Record<string, { releasedInVersion: string }>
): string {
  const allVersions = [
    ...Object.values(tours).map(t => t.releasedInVersion),
    ...Object.values(showcases).map(s => s.releasedInVersion),
  ];
  return allVersions.reduce(
    (max, v) => (v.localeCompare(max, undefined, { numeric: true }) > 0 ? v : max),
    NON_RELEASE_VERSION
  );
}

/**
 * Formats the application version for display.
 * In production, strips RC suffixes. In non-production, shows full version.
 * @param version - The version string (e.g., "2.50.0" or "2.50.0-rc.1")
 * @param stripRcSuffix - Whether to strip RC suffixes (true for production)
 * @returns The formatted version string (e.g., "2.50.0")
 */
export function appVersion(version: string, stripRcSuffix: boolean) {
  return stripRcSuffix ? version?.split('-')[0] : version;
}

/**
 * Current version of the application, derived from package.json version.
 * Format: "major.minor" (e.g., "2.54")
 */
export const currVersion = import.meta.env.VITE_NPM_PACKAGE_VERSION?.split('.').slice(0, 2).join('.'); // eg "2.54"
